/**
 * src/components/booking-modal.jsx
 * FINAL ESDRAS PRODUCTION BUILD - 2026
 * INTEGRATED: Monnify SDK, Esdras Vault Escrow, 90/10 Stylist Split
 */

import React, { useState } from 'react';
import { db, auth } from '../firebase';
import TimeSlotPicker from './timeslotpicker'; 
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';

const formatNaira = (amount) => {
  if (!amount) return "₦0.00";
  return new Intl.NumberFormat('en-NG', { 
    style: 'currency', 
    currency: 'NGN',
    minimumFractionDigits: 0 
  }).format(amount).replace('NGN', '₦');
};

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function BookingModal({ 
  barber, 
  styleName, 
  stylePrice, 
  renderedImageUrl, 
  userHeadScan, 
  onClose 
}) {
  const [bookingStep, setBookingStep] = useState('slot'); 
  const [selectedSlot, setSelectedSlot] = useState(null);
  const user = auth.currentUser;

  const handleMonnifyPayment = () => {
    if (!user) return toast.error('Please login to continue');
    if (!selectedSlot) return toast.error('Please select a time slot');
    
    // Safety check for stylist sub-account
    if (!barber?.monnifyDetails?.subAccountCode && !barber?.monnifySubAccountCode) {
        return toast.error('Stylist payout account not verified. Contact support.');
    }

    const subAccountCode = barber.monnifyDetails?.subAccountCode || barber.monnifySubAccountCode;
    const totalAmount = stylePrice || 8000;

    window.MonnifySDK.initialize({
      amount: totalAmount,
      currency: "NGN",
      reference: `bk_mon_${new Date().getTime()}`,
      customerFullName: user.displayName || "Esdras Client",
      customerEmail: user.email || "customer@esdras.app",
      apiKey: process.env.REACT_APP_MONNIFY_API_KEY,
      contractCode: process.env.REACT_APP_MONNIFY_CONTRACT_CODE,
      paymentDescription: `Esdras Booking: ${styleName} with ${barber.displayName || barber.name}`,
      metadata: {
        stylistId: barber.id,
        slot: selectedSlot.toISOString(),
        styleName: styleName,
        clientId: user.uid
      },
      incomeSplitConfig: [
        {
          "subAccountCode": subAccountCode,
          "feePercentage": 0, 
          "splitPercentage": 90, // Core 90/10 Revenue Split
          "feeBearer": false 
        }
      ],
      onComplete: (response) => {
        if (response.status === 'SUCCESS') {
          saveBookingToFirebase(response.paymentReference);
        } else {
          toast.error("Vault session failed. Please retry.");
        }
      },
      onClose: () => {
        toast.dismiss();
        toast("Transaction Suspended", { icon: '🛡️' });
      }
    });
  };

  const saveBookingToFirebase = async (reference) => {
    const loadingToast = toast.loading("Securing your slot in the Vault...");
    try {
      await addDoc(collection(db, 'bookings'), {
        clientId: user.uid,
        clientEmail: user.email,
        clientName: user.displayName || 'Anonymous Client',
        barberId: barber.id,
        stylistName: barber.displayName || barber.shopName || barber.name,
        styleName: styleName,
        styleImage: renderedImageUrl || null,
        userHeadScan: userHeadScan || null, 
        amount: stylePrice,
        slot: selectedSlot,
        status: 'paid', // Triggers real-time removal from TimeSlotPicker
        escrowStatus: 'held', // Monnify holds funds; Cloud Function releases on completion
        paymentRef: reference,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      
      toast.success("Esdras Vault Secured!", { id: loadingToast });
      setBookingStep('success');
    } catch (error) {
      console.error("Critical Firestore Sync Error:", error);
      toast.error("Vault Secured, but Sync Failed. Contact Support.", { id: loadingToast });
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalContentStyle}>
        <button onClick={onClose} style={closeX}>&times;</button>
        
        {bookingStep === 'slot' ? (
          <>
            <h2 style={titleStyle}>FINALIZING BOOKING</h2>
            
            <div style={summaryBox}>
                <img 
                  src={renderedImageUrl || '/placeholder-style.jpg'} 
                  style={previewImgStyle} 
                  alt="Style Preview" 
                />
                <h3 style={{ color: GOLD, margin: '5px 0', fontSize: '1.2rem' }}>{styleName}</h3>
                <p style={{ opacity: 0.7, fontSize: '0.9rem' }}>
                    Session with {barber.displayName || barber.name}
                </p>
            </div>
            
            <div style={priceTag}>{formatNaira(stylePrice)}</div>
            
            <div style={pickerContainer}>
              <TimeSlotPicker stylistId={barber.id} onSlotSelected={setSelectedSlot} />
            </div>

            <button 
              onClick={handleMonnifyPayment} 
              disabled={!selectedSlot} 
              style={{...actionBtn, opacity: selectedSlot ? 1 : 0.5}}
            >
              {selectedSlot ? 'SECURE BOOKING' : 'CHOOSE A TIME SLOT'}
            </button>
            
            <p style={escrowDisclaimer}>
                🛡️ <b>Esdras Vault Protection:</b> Funds are held in escrow and only released to the stylist after you confirm the session is complete.
            </p>
          </>
        ) : (
          <div style={{ padding: '2rem 0' }}>
            <div style={successIcon}>✓</div>
            <h2 style={{ color: GOLD, letterSpacing: '2px', fontWeight: '800' }}>VAULT SECURED</h2>
            <p style={{ opacity: 0.8, margin: '20px 0', lineHeight: '1.6' }}>
                Payment successful. Your funds are now in escrow.<br /><br />
                We have notified <strong>{barber.displayName || barber.name}</strong>.
            </p>
            <button onClick={onClose} style={secBtn}>RETURN TO PROFILE</button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- STYLESHEET ---
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(12px)', display: 'grid', placeItems: 'center', zIndex: 10000, padding: '20px' };
const modalContentStyle = { background: NAVY, color: 'white', padding: '2.5rem 2rem', borderRadius: '35px', border: `1px solid rgba(184, 134, 11, 0.4)`, maxWidth: '440px', width: '100%', textAlign: 'center', position: 'relative', boxShadow: '0 25px 60px rgba(0,0,0,0.8)', fontFamily: 'Montserrat, sans-serif' };
const closeX = { position: 'absolute', top: '20px', right: '25px', background: 'none', border: 'none', color: 'white', fontSize: '1.8rem', cursor: 'pointer', opacity: 0.6 };
const titleStyle = { fontSize: '1rem', fontWeight: '900', letterSpacing: '4px', marginBottom: '25px', color: GOLD, textTransform: 'uppercase' };
const summaryBox = { background: 'rgba(255,255,255,0.03)', padding: '20px', borderRadius: '25px', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.08)' };
const previewImgStyle = { width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${GOLD}`, marginBottom: '15px', boxShadow: `0 0 20px rgba(184, 134, 11, 0.2)` };
const priceTag = { fontSize: '3rem', fontWeight: '800', color: GOLD, margin: '10px 0', fontFamily: 'Playfair Display, serif' };
const pickerContainer = { margin: '25px 0', maxHeight: '350px', overflowY: 'auto', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '15px' };
const actionBtn = { background: GOLD, color: 'black', padding: '20px', border: 'none', borderRadius: '50px', fontSize: '1.1rem', fontWeight: '900', width: '100%', cursor: 'pointer', marginTop: '10px', transition: '0.3s ease' };
const secBtn = { ...actionBtn, background: 'transparent', color: GOLD, border: `2px solid ${GOLD}` };
const escrowDisclaimer = { fontSize: '0.75rem', opacity: 0.6, marginTop: '20px', lineHeight: '1.4', padding: '0 10px' };
const successIcon = { width: '90px', height: '90px', background: GOLD, color: 'black', borderRadius: '50%', display: 'grid', placeItems: 'center', margin: '0 auto 25px', fontSize: '3rem' };
